import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Award, TrendingUp, Sparkles, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { useT } from '../contexts/LanguageContext';

const GRADE_COLOR = {
  A: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  B: 'bg-blue-100 text-blue-700 border-blue-300',
  C: 'bg-amber-100 text-amber-700 border-amber-300',
  D: 'bg-orange-100 text-orange-700 border-orange-300',
  F: 'bg-red-100 text-red-700 border-red-300'
};

export default function ServiceProviderKPI() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const canEvaluate = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);

  useEffect(() => { fetchKPI(); }, []);

  const fetchKPI = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/service-providers/kpi`, { withCredentials: true });
      setRows(r.data);
    } catch { toast.error('Failed to load KPI data'); }
    setLoading(false);
  };

  const evaluateAI = async (providerId) => {
    setAiBusy(true); setAiOpen(true); setAiResult(null);
    try {
      const r = await axios.post(`${API}/service-providers/${providerId}/ai-evaluate`, {}, { withCredentials: true });
      setAiResult(r.data);
    } catch (e) {
      setAiResult({ narrative: e.response?.data?.detail || 'AI evaluation failed' });
    }
    setAiBusy(false);
  };

  return (
    <Layout>
      <div data-testid="kpi-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Award className="w-6 h-6 text-teal-600" />{t('kpi.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('kpi.subtitle')}</p>
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : rows.length === 0 ? (
          <Card className="p-12 text-center">
            <Award className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No service providers yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add suppliers and assign them to AMC contracts to see KPI scores.</p>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {rows.map(r => (
              <Card key={r.service_provider_id} data-testid={`kpi-card-${r.service_provider_id}`} className="p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-foreground truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {r.active_contracts}/{r.total_contracts} active contracts · {r.tasks_total} tasks
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full border text-sm font-bold ${GRADE_COLOR[r.grade] || 'bg-slate-100 text-slate-700'}`}>
                    {r.grade}
                  </div>
                </div>

                {/* KPI score bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">KPI Score</span>
                    <span className="font-bold text-foreground">{r.kpi_score}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${r.kpi_score >= 75 ? 'bg-emerald-500' : r.kpi_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(100, r.kpi_score)}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs mb-4">
                  <div className="p-2 rounded bg-slate-50 text-center">
                    <div className="text-muted-foreground">Completion</div>
                    <div className="font-bold text-foreground">{r.completion_pct}%</div>
                  </div>
                  <div className="p-2 rounded bg-slate-50 text-center">
                    <div className="text-muted-foreground">SLA Hit</div>
                    <div className="font-bold text-foreground">{r.sla_hit_pct != null ? r.sla_hit_pct + '%' : 'N/A'}</div>
                  </div>
                  <div className="p-2 rounded bg-slate-50 text-center">
                    <div className="text-muted-foreground">Overdue</div>
                    <div className={`font-bold ${r.tasks_overdue > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{r.tasks_overdue}</div>
                  </div>
                </div>

                {canEvaluate && (
                  <Button data-testid={`ai-eval-${r.service_provider_id}`} onClick={() => evaluateAI(r.service_provider_id)} size="sm" className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white">
                    <Sparkles className="w-3.5 h-3.5 mr-1" />AI Evaluation
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}

        <Dialog open={aiOpen} onOpenChange={setAiOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />AI Performance Evaluation
              </DialogTitle>
            </DialogHeader>
            {aiBusy ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 mx-auto text-purple-600 animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">Claude Sonnet is analyzing performance...</p>
              </div>
            ) : aiResult && (
              <div className="space-y-3 mt-2">
                {aiResult.kpi && (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 rounded bg-slate-50">
                      <div className="text-muted-foreground">Score</div>
                      <div className="font-bold text-foreground text-base">{aiResult.kpi.kpi_score}%</div>
                    </div>
                    <div className="p-2 rounded bg-slate-50">
                      <div className="text-muted-foreground">Grade</div>
                      <div className={`font-bold text-base ${aiResult.kpi.grade === 'A' ? 'text-emerald-600' : aiResult.kpi.grade === 'F' ? 'text-red-600' : 'text-amber-600'}`}>{aiResult.kpi.grade}</div>
                    </div>
                    <div className="p-2 rounded bg-slate-50">
                      <div className="text-muted-foreground">Completion</div>
                      <div className="font-bold text-foreground text-base">{aiResult.kpi.completion_pct}%</div>
                    </div>
                  </div>
                )}
                <div data-testid="ai-narrative" className="p-4 rounded-lg bg-purple-50/50 border border-purple-200 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {aiResult.narrative}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
